import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // PDF 題號 ↔ JSON 題號對照：
  //   2=q12 / 2a=q13 / 2b=q14 / 4=q16 / 4a=q17 / 6=q19 / 6a=q20
  //   10=q24 / 10a=q25 / 12=q27 / 12a=q28 / 14=q30 / 14a=q31 / 14b=q32 / 14c=q33
  const visibleIfConditionsInit = convertVisibleIf({
    // 2. 沒有工作(1) → 續問 2a；有工作(2) → 續問 2b
    q13: '{q12} = 1',
    q14: '{q12} = 2',
    // 4. 沒有其他人輪替照顧(2) → 續問 4a
    q17: '{q16} = 2',
    // 6. 有其他需協助家人 → 續問 6a；沒有(1) 則跳問第 7 題
    //    「(2)有，__位」是 o_type 6，值為 "other"，不可寫成 2
    q20: '{q19} = "other"',
    // 10. 生活有改變(2) → 續問 10a；沒有改變(1) 跳問第 11 題
    q25: '{q24} = 2',
    // 12. 勾選「(7)都沒有使用/接受上述照顧服務資源」→ 續問 12a
    //     該選項是互斥選項（cf.parameters.group=[6]），值為 "none"，不可寫成 7
    q28: '{q27} anyof ["none"]',
    // 14. 願意(1) → 續問 14a、14b，答完即結束
    q31: '{q30} = 1',
    q32: '{q30} = 1',
    // 14. 不願意或曾申請但未符合需要(2) → 跳問 14c
    //     無意見(3) → 14a/14b/14c 皆不顯示，問卷自然結束
    q33: '{q30} = 2',
  })
  // 非必答題
  const notRequired = convertNotRequired([
    5, // E.可接受複查時段(可複選)
  ])
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 本案為政府委託訪問調查，結語採客戶指定文案，不使用 EZChoice 預設結語
  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您撥冗填答。<br>如有填卷問題請洽典通股份有限公司 黃小姐 02-23315133#813<br><br>中高齡就業相關的補助措施請參考下列網址:<br><a href="https://45plus.wda.gov.tw/News2.aspx?n=3&amp;sms=10290" target="_blank">https://45plus.wda.gov.tw/News2.aspx?n=3&amp;sms=10290</a></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
